import { FilterContext } from "~/providers/FilterProvider";
import { ComponentProps } from "@ts/components";
import { SearchResultSwatch } from "@ts/search";
import { useFilterListProps } from "@usereactify/search";
import React, { useContext, useMemo, type FC } from "react";
import { useRouteLoaderData } from "@hooks/useRouteLoaderData";

export type SizeType = {
  _key: string;
  bared: string;
  us_au: string;
  cm: string;
  inch: string;
};

type FilterSectionBoxInputProps = ComponentProps &
  ReturnType<typeof useFilterListProps> & {
    handleSizeClick?: () => void;
    hideSizeGuide?: boolean;
  };

type FilterSectionBoxOutputProps = FilterSectionBoxInputProps & {
  options: SearchResultSwatch[];
  convertedSizing: SizeType[];
  handleClick: () => void;
  layout?: "heroBanner";
};

const prepareBoxOptions = (
  options: SearchResultSwatch[],
): SearchResultSwatch[] => {
  const data =
    options?.reduce((result: { [key: string]: SearchResultSwatch }, item) => {
      result[item.key] = item;

      return result;
    }, {}) || {};

  return Object.values<SearchResultSwatch>(data);
};

export const withFilterSectionBox =
  (Component: FC<FilterSectionBoxOutputProps>) =>
  ({
    name = "FilterSectionBox",
    options,
    handleSizeClick,
    ...props
  }: FilterSectionBoxInputProps) => {
    const { gender, handleScroll } = useContext(FilterContext);
    const { sizeGuide } = useRouteLoaderData("routes/$site");

    const convertedSizing: SizeType[] = useMemo(() => {
      if (gender === "men") {
        return sizeGuide.men;
      } else {
        return sizeGuide.women;
      }
    }, [gender, sizeGuide]);

    const boxOptions = prepareBoxOptions(options) || [];

    const handleClick = () => {
      handleScroll();

      if (handleSizeClick) handleSizeClick();
    };

    Component.displayName = name;

    return (
      <Component
        {...props}
        options={boxOptions}
        convertedSizing={convertedSizing}
        handleClick={handleClick}
      />
    );
  };
